import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  useGetEnglishQuizzesQuery,
  useDeleteEnglishQuizMutation,
  usePublishEnglishQuizMutation,
  useUnpublishEnglishQuizMutation,
  useArchiveEnglishQuizMutation,
  useActivateEnglishQuizMutation,
} from "../../slices/englishQuizApiSlice";

import EnglishQuizTable from "./components/EnglishQuizTable";
import EnglishQuizCard from "./components/EnglishQuizCard";
import EnglishQuizFilters from "./components/EnglishQuizFilters";
import EnglishQuizHeader from "./components/EnglishQuizHeader";
import EnglishQuizDeleteDialog from "./components/EnglishQuizDeleteDialog";

const EnglishQuizList = () => {
  // ======================================================
  // Navigation
  // ======================================================

  const navigate = useNavigate();

  // ======================================================
  // State
  // ======================================================

  const [viewMode, setViewMode] = useState("table");

  const [filters, setFilters] = useState({
    search: "",
    subject: "",
    status: "",
  });

  const [deleteDialogOpen, setDeleteDialogOpen] =
    useState(false);

  const [selectedQuiz, setSelectedQuiz] =
    useState(null);

  const [actionError, setActionError] =
    useState(null);

  // ======================================================
  // Queries
  // ======================================================

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetEnglishQuizzesQuery();

  // ======================================================
  // Mutations
  // ======================================================

  const [
    deleteEnglishQuiz,
    {
      isLoading: isDeleting,
      error: deleteError,
    },
  ] = useDeleteEnglishQuizMutation();

  const [
    publishEnglishQuiz,
    {
      isLoading: isPublishing,
    },
  ] = usePublishEnglishQuizMutation();

  const [
    unpublishEnglishQuiz,
    {
      isLoading: isUnpublishing,
    },
  ] = useUnpublishEnglishQuizMutation();

  const [
    archiveEnglishQuiz,
    {
      isLoading: isArchiving,
    },
  ] = useArchiveEnglishQuizMutation();

  const [
    activateEnglishQuiz,
    {
      isLoading: isActivating,
    },
  ] = useActivateEnglishQuizMutation();

  // ======================================================
  // Normalize API Response
  // ======================================================

  const quizzes = useMemo(() => {
    if (Array.isArray(data)) {
      return data;
    }

    if (Array.isArray(data?.quizzes)) {
      return data.quizzes;
    }

    if (Array.isArray(data?.data)) {
      return data.data;
    }

    return [];
  }, [data]);

  // ======================================================
  // Filter Quizzes
  // ======================================================

  const filteredQuizzes = useMemo(() => {
  const search = String(filters.search || "")
    .trim()
    .toLowerCase();

  return quizzes.filter((quiz) => {
    const quizName = String(quiz?.name ?? "").toLowerCase();

    const description = String(
      quiz?.description ?? "",
    ).toLowerCase();

    const subject = String(
      quiz?.questionBankSubject ??
        quiz?.subject ??
        "",
    ).toLowerCase();

    const status = String(
      quiz?.status ?? "",
    ).toLowerCase();

    const matchesSearch =
      !search ||
      quizName.includes(search) ||
      description.includes(search) ||
      subject.includes(search);

    const matchesSubject =
      !filters.subject ||
      subject ===
        String(filters.subject).toLowerCase();

    const matchesStatus =
      !filters.status ||
      status ===
        String(filters.status).toLowerCase();

    return (
      matchesSearch &&
      matchesSubject &&
      matchesStatus
    );
  });
}, [quizzes, filters]);

  // ======================================================
  // Error Message
  // ======================================================

  const getErrorMessage = (value) => {
    if (!value) {
      return "";
    }

    if (typeof value === "string") {
      return value;
    }

    return (
      value?.data?.message ||
      value?.message ||
      "Something went wrong. Please try again."
    );
  };

  // ======================================================
  // Filter Change
  // ======================================================

  const handleFiltersChange = (nextFilters) => {
    setFilters((current) => ({
      ...current,
      ...(nextFilters || {}),
    }));
  };

  // ======================================================
  // Clear Filters
  // ======================================================

  const handleClearFilters = () => {
    setFilters({
      search: "",
      subject: "",
      status: "",
    });
  };

  // ======================================================
  // Delete
  // ======================================================

  const handleOpenDelete = (quiz) => {
    setActionError(null);
    setSelectedQuiz(quiz);
    setDeleteDialogOpen(true);
  };

  const handleCloseDelete = () => {
    if (isDeleting) {
      return;
    }

    setDeleteDialogOpen(false);
    setSelectedQuiz(null);
    setActionError(null);
  };

  const handleDelete = async () => {
    if (!selectedQuiz?._id) {
      return;
    }

    try {
      setActionError(null);

      await deleteEnglishQuiz(
        selectedQuiz._id,
      ).unwrap();

      setDeleteDialogOpen(false);
      setSelectedQuiz(null);

      await refetch();
    } catch (mutationError) {
      setActionError(mutationError);
    }
  };

  // ======================================================
  // Publish
  // ======================================================

  const handlePublish = async (quiz) => {
    if (!quiz?._id) {
      return;
    }

    try {
      setActionError(null);

      await publishEnglishQuiz(
        quiz._id,
      ).unwrap();

      await refetch();
    } catch (mutationError) {
      setActionError(mutationError);
    }
  };

  // ======================================================
  // Unpublish
  // ======================================================

  const handleUnpublish = async (quiz) => {
    if (!quiz?._id) {
      return;
    }

    try {
      setActionError(null);

      await unpublishEnglishQuiz(
        quiz._id,
      ).unwrap();

      await refetch();
    } catch (mutationError) {
      setActionError(mutationError);
    }
  };

  // ======================================================
  // Archive
  // ======================================================

  const handleArchive = async (quiz) => {
    if (!quiz?._id) {
      return;
    }

    try {
      setActionError(null);

      await archiveEnglishQuiz(
        quiz._id,
      ).unwrap();

      await refetch();
    } catch (mutationError) {
      setActionError(mutationError);
    }
  };

  // ======================================================
  // Activate
  // ======================================================

  const handleActivate = async (quiz) => {
    if (!quiz?._id) {
      return;
    }

    try {
      setActionError(null);

      await activateEnglishQuiz(
        quiz._id,
      ).unwrap();

      await refetch();
    } catch (mutationError) {
      setActionError(mutationError);
    }
  };

  // ======================================================
  // Navigation Actions
  // ======================================================

  const handleView = (quiz) => {
    if (!quiz?._id) {
      return;
    }

    navigate(`/english-quizzes/${quiz._id}`);
  };

  const handleEdit = (quiz) => {
    if (!quiz?._id) {
      return;
    }

    navigate(`/english-quizzes/${quiz._id}/edit`);
  };

  const handleCreate = () => {
    navigate("/english-quizzes/create");
  };

  const handleBlueprint = (quiz) => {
    if (!quiz?._id) {
      return;
    }

    navigate(
      `/english-quizzes/${quiz._id}/blueprint`,
    );
  };

  // ======================================================
  // Refresh
  // ======================================================

  const handleRefresh = async () => {
    try {
      await refetch();
    } catch {
      // RTK Query exposes the error through query state.
    }
  };

  // ======================================================
  // Loading
  // ======================================================

  if (isLoading && !data) {
    return (
      <div className="space-y-6">
        <EnglishQuizHeader
          onCreate={handleCreate}
          onRefresh={handleRefresh}
          isRefreshing
        />

        <EnglishQuizFilters
          filters={filters}
          onChange={handleFiltersChange}
          onClear={handleClearFilters}
          disabled
        />

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="space-y-4">
            {Array.from({ length: 6 }).map(
              (_, index) => (
                <div
                  key={index}
                  className="h-16 animate-pulse rounded-xl bg-slate-100"
                />
              ),
            )}
          </div>
        </div>
      </div>
    );
  }

  // ======================================================
  // Query Error
  // ======================================================

  if (isError && !data) {
    return (
      <div className="rounded-2xl border border-red-200 bg-white p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle
                cx="12"
                cy="12"
                r="9"
              />

              <path
                strokeLinecap="round"
                d="M12 8v5"
              />

              <path
                strokeLinecap="round"
                d="M12 16h.01"
              />
            </svg>
          </div>

          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-red-900">
              Unable to load English quizzes
            </h2>

            <p className="mt-1 text-sm text-red-700">
              {getErrorMessage(error)}
            </p>

            <button
              type="button"
              onClick={handleRefresh}
              className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ======================================================
  // Render
  // ======================================================

  return (
    <div className="space-y-6">
      {/* ==================================================
          Header
      ================================================== */}

      <EnglishQuizHeader
        onCreate={handleCreate}
        onRefresh={handleRefresh}
        isRefreshing={isFetching}
      />

      {/* ==================================================
          Action Error
      ================================================== */}

      {actionError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <div className="flex items-center gap-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 shrink-0 text-red-600"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle
                cx="12"
                cy="12"
                r="9"
              />

              <path
                strokeLinecap="round"
                d="M12 8v5"
              />

              <path
                strokeLinecap="round"
                d="M12 16h.01"
              />
            </svg>

            <p className="text-sm font-medium text-red-800">
              {getErrorMessage(actionError)}
            </p>

            <button
              type="button"
              onClick={() =>
                setActionError(null)
              }
              className="ml-auto text-sm font-medium text-red-700 hover:text-red-900"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* ==================================================
          Filters
      ================================================== */}

      <EnglishQuizFilters
        filters={filters}
        onChange={handleFiltersChange}
        onClear={handleClearFilters}
        disabled={isFetching}
      />

      {/* ==================================================
          Result Count
      ================================================== */}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-slate-500">
            Showing{" "}
            <span className="font-semibold text-slate-700">
              {filteredQuizzes.length}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-slate-700">
              {quizzes.length}
            </span>{" "}
            quizzes
          </p>
        </div>

        {isFetching && (
          <div className="inline-flex items-center gap-2 text-xs text-slate-500">
            <span className="h-2 w-2 animate-pulse rounded-full bg-indigo-500" />
            Updating...
          </div>
        )}
      </div>

      {/* ==================================================
          Empty State
      ================================================== */}

      {!filteredQuizzes.length && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v13a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 18.5v-13Z"
              />

              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 8h8M8 12h5"
              />
            </svg>
          </div>

          <h3 className="mt-4 text-base font-semibold text-slate-900">
            {quizzes.length
              ? "No quizzes match your filters"
              : "No English quizzes yet"}
          </h3>

          <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
            {quizzes.length
              ? "Try changing or clearing your search and filters."
              : "Create your first English quiz to start building your quiz library."}
          </p>

          <div className="mt-5 flex flex-wrap justify-center gap-3">
            {quizzes.length > 0 && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
              >
                Clear filters
              </button>
            )}

            <button
              type="button"
              onClick={handleCreate}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Create English Quiz
            </button>
          </div>
        </div>
      )}

      {/* ==================================================
          Table View
      ================================================== */}

      {filteredQuizzes.length > 0 &&
        viewMode === "table" && (
          <EnglishQuizTable
            quizzes={filteredQuizzes}
            isLoading={isFetching}
            onView={handleView}
            onEdit={handleEdit}
            // onDelete={handleOpenDelete}
            onPublish={handlePublish}
            onUnpublish={handleUnpublish}
            onArchive={handleArchive}
            onActivate={handleActivate}
            onBlueprint={handleBlueprint}
            isPublishing={isPublishing}
            isUnpublishing={isUnpublishing}
            isArchiving={isArchiving}
            isActivating={isActivating}
          />
        )}

      {/* ==================================================
          Card View
      ================================================== */}

      {filteredQuizzes.length > 0 &&
        viewMode === "card" && (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {filteredQuizzes.map((quiz) => (
              <EnglishQuizCard
                key={quiz._id}
                quiz={quiz}
                onView={() =>
                  handleView(quiz)
                }
                onEdit={() =>
                  handleEdit(quiz)
                }
                // onDelete={() =>
                //   handleOpenDelete(quiz)
                // }
                onPublish={() =>
                  handlePublish(quiz)
                }
                onUnpublish={() =>
                  handleUnpublish(quiz)
                }
                onArchive={() =>
                  handleArchive(quiz)
                }
                onActivate={() =>
                  handleActivate(quiz)
                }
                onBlueprint={() =>
                  handleBlueprint(quiz)
                }
                isPublishing={
                  isPublishing
                }
                isUnpublishing={
                  isUnpublishing
                }
                isArchiving={
                  isArchiving
                }
                isActivating={
                  isActivating
                }
              />
            ))}
          </div>
        )}

      {/* ==================================================
          Delete Dialog
      ================================================== */}

      <EnglishQuizDeleteDialog
        isOpen={deleteDialogOpen}
        quiz={selectedQuiz}
        onClose={handleCloseDelete}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        error={
          actionError || deleteError
        }
      />
    </div>
  );
};

export default EnglishQuizList;